// ./src/components/generic/ZonesEditor/ZonesEditor.tsx
import React from "react";
import {
  Stack,
  Group,
  Text,
  Button,
  ActionIcon,
  Paper,
  NumberInput,
  Alert,
  Box,
  Progress,
  TextInput,
} from "@mantine/core";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { AppIcon } from "@/components/generic";

export interface ZoneItem {
  id: string;
  widthPercent: number;
}

export interface ZonesEditorProps {
  /** Current zones array */
  value: ZoneItem[] | null | undefined;

  /** Callback when zones change */
  onChange: (value: ZoneItem[]) => void;

  /** Label for the editor */
  label?: string;

  /** Description text */
  description?: string;
}

export const ZonesEditor: React.FC<ZonesEditorProps> = ({
  value,
  onChange,
  label,
  description,
}) => {
  const zones = value || [];

  // Calculate total width percentage
  const totalWidth = zones.reduce((sum, zone) => sum + zone.widthPercent, 0);
  const isValidTotal = totalWidth === 100;

  // Generate unique ID for new zones based on zone number
  const generateZoneId = (): string => {
    const existingNumbers = zones
      .map((zone) => {
        const match = zone.id.match(/^zone-(\d+)$/);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter((num) => !isNaN(num));

    const maxNumber =
      existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
    return `zone-${maxNumber + 1}`;
  };

  // Handle drag end
  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(zones);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    onChange(items);
  };

  // Add new zone
  const handleAddZone = () => {
    // Calculate width for new zone
    const remainingWidth = Math.max(0, 100 - totalWidth);
    const newWidth = remainingWidth > 0 ? remainingWidth : 33;

    // If we need to take space, distribute evenly
    let updatedZones = [...zones];
    if (remainingWidth <= 0 && zones.length > 0) {
      const reductionPerZone = Math.floor(33 / zones.length);
      updatedZones = zones.map((zone) => ({
        ...zone,
        widthPercent: Math.max(10, zone.widthPercent - reductionPerZone),
      }));
    }

    const newZone: ZoneItem = {
      id: generateZoneId(),
      widthPercent: newWidth,
    };

    onChange([...updatedZones, newZone]);
  };

  // Remove zone
  const handleRemoveZone = (index: number) => {
    const items = zones.filter((_, i) => i !== index);
    onChange(items);
  };

  // Update zone ID
  const handleIdChange = (index: number, newId: string) => {
    const items = [...zones];
    items[index] = { ...items[index], id: newId };
    onChange(items);
  };

  // Update zone width
  const handleWidthChange = (index: number, width: number | string) => {
    const numWidth = typeof width === "string" ? parseInt(width, 10) : width;
    if (isNaN(numWidth)) return;

    const items = [...zones];
    items[index] = {
      ...items[index],
      widthPercent: Math.max(0, Math.min(100, numWidth)),
    };
    onChange(items);
  };

  // Auto-distribute widths evenly
  const handleDistributeEvenly = () => {
    if (zones.length === 0) return;

    const widthPerZone = Math.floor(100 / zones.length);
    const remainder = 100 - widthPerZone * zones.length;

    const items = zones.map((zone, index) => ({
      ...zone,
      widthPercent: widthPerZone + (index === 0 ? remainder : 0),
    }));

    onChange(items);
  };

  // Check for duplicate IDs
  const getDuplicateIds = (): Set<string> => {
    const idCounts = new Map<string, number>();
    zones.forEach((zone) => {
      idCounts.set(zone.id, (idCounts.get(zone.id) || 0) + 1);
    });
    return new Set(
      Array.from(idCounts.entries())
        .filter(([_, count]) => count > 1)
        .map(([id]) => id)
    );
  };

  const duplicateIds = getDuplicateIds();
  const hasDuplicates = duplicateIds.size > 0;

  return (
    <Stack gap="sm">
      {label && (
        <div>
          <Text size="sm" fw={500}>
            {label}
          </Text>
          {description && (
            <Text size="xs" c="dimmed" mt={2}>
              {description}
            </Text>
          )}
        </div>
      )}

      {/* Total width indicator */}
      {zones.length > 0 && (
        <Box>
          <Group justify="space-between" mb={4}>
            <Text size="xs" c="dimmed">
              Total Width
            </Text>
            <Text size="xs" fw={600} c={isValidTotal ? "green" : "red"}>
              {totalWidth}%
            </Text>
          </Group>
          <Progress
            value={totalWidth}
            color={isValidTotal ? "green" : "red"}
            size="sm"
          />
          {!isValidTotal && (
            <Alert
              icon={<AppIcon iconName="AlertCircle" size={16} />}
              color="yellow"
              mt="xs"
              p="xs"
            >
              <Text size="xs">
                Total width should be 100%. Currently {totalWidth}%.{" "}
                <Text
                  component="span"
                  c="blue"
                  style={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={handleDistributeEvenly}
                >
                  Distribute evenly
                </Text>
              </Text>
            </Alert>
          )}
          {hasDuplicates && (
            <Alert
              icon={<AppIcon iconName="AlertCircle" size={16} />}
              color="red"
              mt="xs"
              p="xs"
            >
              <Text size="xs">
                Duplicate zone IDs detected. Each zone must have a unique ID.
              </Text>
            </Alert>
          )}
        </Box>
      )}

      {/* Zones list */}
      {zones.length > 0 ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="zones-list">
            {(provided) => (
              <Stack
                gap="xs"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {zones.map((zone, index) => {
                  const isDuplicate = duplicateIds.has(zone.id);

                  return (
                    <Draggable
                      key={`zone-item-${index}`}
                      draggableId={`zone-item-${index}`}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <Paper
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          withBorder
                          p="sm"
                          style={{
                            ...provided.draggableProps.style,
                            backgroundColor: snapshot.isDragging
                              ? "var(--mantine-color-blue-0)"
                              : undefined,
                            borderColor: isDuplicate
                              ? "var(--mantine-color-red-5)"
                              : undefined,
                          }}
                        >
                          <Group justify="space-between" wrap="nowrap" gap="sm">
                            {/* Drag handle */}
                            <div {...provided.dragHandleProps}>
                              <AppIcon
                                iconName="GripVertical"
                                size={16}
                                style={{ cursor: "grab", color: "gray" }}
                              />
                            </div>

                            {/* Zone ID input */}
                            <TextInput
                              value={zone.id}
                              onChange={(e) =>
                                handleIdChange(index, e.target.value)
                              }
                              placeholder="zone-1"
                              size="sm"
                              style={{ flex: 1 }}
                              leftSection={<AppIcon iconName="Tag" size={16} />}
                              error={isDuplicate ? "Duplicate ID" : undefined}
                            />

                            {/* Width input */}
                            <NumberInput
                              value={zone.widthPercent}
                              onChange={(value) =>
                                handleWidthChange(index, value)
                              }
                              min={0}
                              max={100}
                              suffix="%"
                              size="sm"
                              style={{ width: 100 }}
                              allowNegative={false}
                              allowDecimal={false}
                            />

                            {/* Remove button */}
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() => handleRemoveZone(index)}
                              size="sm"
                            >
                              <AppIcon iconName="Trash" size={16} />
                            </ActionIcon>
                          </Group>
                        </Paper>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Paper withBorder p="md">
          <Text size="sm" c="dimmed" ta="center">
            No zones added. Click "Add Zone" to get started.
          </Text>
        </Paper>
      )}

      {/* Add zone button */}
      <Button
        variant="light"
        leftSection={<AppIcon iconName="Plus" size={16} />}
        onClick={handleAddZone}
        size="sm"
      >
        Add Zone
      </Button>
    </Stack>
  );
};
